import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Ship, MapPinned, Boxes, Users, ScrollText,
  ListChecks, History, Waves, CalendarRange, Sparkles, Scale, CircleHelp
} from "lucide-react";
import { TESTIDS } from "@/constants/testIds";
import { useAppAuth } from "@/auth/AppAuthProvider";

const navItems = [
  { to: "/", label: "Operations", icon: LayoutDashboard, tid: TESTIDS.navDashboard, exact: true },
  { to: "/terminals", label: "Terminals & Berths", icon: MapPinned, tid: TESTIDS.navTerminals },
  { to: "/vessels", label: "Vessels & Ship Assets", icon: Ship, tid: TESTIDS.navVessels },
  { to: "/port-calls", label: "Port Calls", icon: ListChecks, tid: TESTIDS.navPortCalls },
  { to: "/timeline", label: "Timeline", icon: CalendarRange, tid: "nav-timeline" },
  { to: "/assets", label: "Terminal Assets", icon: Boxes, tid: TESTIDS.navAssets },
  { to: "/personnel", label: "Personnel", icon: Users, tid: TESTIDS.navPersonnel, capability: "case.confidential.read" },
  { to: "/sops", label: "SOP Rules", icon: ScrollText, tid: TESTIDS.navSops },
  { to: "/labor-knowledge", label: "Labour Knowledge", icon: Scale, tid: "nav-labor-knowledge" },
  { to: "/vision", label: "AI Vision Metrics", icon: Sparkles, tid: "nav-vision", capability: "map.manage" },
  { to: "/audit", label: "Audit Log", icon: History, tid: TESTIDS.navAudit, capability: "audit.read" },
  { to: "/help", label: "Help & Playbooks", icon: CircleHelp, tid: TESTIDS.navHelp },
];

export default function Sidebar() {
  const { hasCapability } = useAppAuth();
  const loc = useLocation();
  const isActive = (item) =>
    item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);

  return (
    <aside className="w-64 flex-shrink-0 border-r border-[#1E293B] bg-[#050B14] h-screen fixed left-0 top-0 z-30 flex flex-col">
      <div className="h-14 flex items-center px-4 border-b border-[#1E293B]">
        <div className="flex items-center gap-2">
          <svg viewBox="0 0 64 64" width="32" height="32" aria-hidden="true" data-testid="brand-logo" className="flex-shrink-0">
            <rect x="0" y="0" width="64" height="64" rx="6" fill="#050B14" />
            <rect x="1" y="1" width="62" height="62" rx="6" fill="none" stroke="#38BDF8" strokeWidth="2" />
            <path d="M64 0 L64 18 L46 0 Z" fill="#0EA5E9" />
            <text x="32" y="42" textAnchor="middle" fontFamily="'Arial Black', 'Helvetica Neue', Helvetica, Arial, sans-serif" fontWeight="900" fontSize="24" letterSpacing="-1.5" fill="#F8FAFC">PHQ</text>
          </svg>
          <div>
            <div className="font-heading font-black tracking-tighter text-slate-50 text-sm leading-none" data-testid="brand-name">PORTCALL</div>
            <div className="font-mono text-[10px] text-sky-400/80 tracking-widest">HQ</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto py-3">
        {navItems.filter(item => !item.capability || hasCapability(item.capability)).map((item) => {
          const active = isActive(item);
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.tid}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors border-l-2 ${
                active
                  ? "bg-[#0E1522] border-l-sky-400 text-slate-50"
                  : "border-l-transparent text-slate-400 hover:bg-[#0E1522] hover:text-slate-200"
              }`}
            >
              <Icon size={16} strokeWidth={1.5} />
              <span className="font-medium tracking-tight">{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="border-t border-[#1E293B] px-4 py-3">
        <div className="flex items-center gap-2 text-xs">
          <Waves size={14} className="text-sky-400" />
          <span className="text-slate-400 font-mono">SYS ONLINE</span>
          <span className="ml-auto inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
        </div>
      </div>
    </aside>
  );
}
